import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Col,
  Form,
  FormInstance,
  Grid,
  Radio,
  RadioGroup,
  Row,
  Schema,
  Text,
} from 'rsuite';
import FlowLayout from '../../../components/layout/FlowLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetTaxCertState,
  updateTaxCertApplicationData,
  clearTaxCertDataExceptProperty,
} from '../../../store/slices/taxCertificate/taxCertificateSlice';
import { selectTaxCertApplicationData } from '../../../store/slices/taxCertificate/taxCertificateSelectors';
import {
  selectConfigExpressApplyParcel,
  selectConfigExpressApplyUnitNumberRequired,
  selectConfigExpressApplyUnitNumberDescription,
} from '../../../store/slices/config/configSelectors';
import { CityRestrictedAddressLookup } from '../../../components/form/AddressLookup';
import FormTextField from '../../../components/form/FormTextField';
import Notice from '../../../components/user-feedback/Notice';
import SelectedProperty from '../../../components/apply/SelectedProperty';
import { lookupAPNAddress } from '../../../api/apiAccess';
import { isNullUndefinedOrEmptyObject } from '../../../common/utils';
import { FLOWS_ENUM, UNIT_NUMBER_STATUS_ENUM } from '../../../common/enums';
import {
  selectCurrentStep,
  selectNextStepPath,
} from '../../../store/slices/flow/flowSelectors';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from '../../../store/slices/flow/flowSlice';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import IconIndicator from '../../../components/user-feedback/IconIndicator';
import { useDebounce } from 'src/hooks/useDebounce';
import { constructMarkup } from '../../../common/utils';
import { PropertyDetails } from '../../../types/flow';

interface AddressLookupResult {
  address: string;
  apn: string[];
}

interface FormValues {
  propertyaddress: string;
  propertyid: string;
  unitnumber: string;
}

const TaxCertProperty = () => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const selectedCurrentStep = useSelector(selectCurrentStep);
  const selectedNextStepPath = useSelector(selectNextStepPath);
  const form = useRef<FormInstance | null>(null);
  const [formError, setFormError] = useState<Record<string, string>>({});
  const [formValues, setFormValues] = useState<FormValues>({
    propertyaddress: '',
    propertyid: '',
    unitnumber: '',
  });
  const [pageError, setPageError] = useState(false);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<
    Partial<PropertyDetails>
  >({});
  const [apn, setApn] = useState<string>('');
  const debouncedApn = useDebounce(apn);
  const [showAddressLookup, setShowAddressLookup] = useState(false);
  const [showKnownField, setShowKnownField] = useState(false);
  const [parcelNotFound, setParcelNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [parcelLookupLoading, setParcelLookupLoading] = useState(false);
  const [lookupInstructions, setLookupInstructions] = useState('');

  const selectedParcelConfig = useSelector(selectConfigExpressApplyParcel);
  const selectedUnitNumberRequired = useSelector(
    selectConfigExpressApplyUnitNumberRequired
  );
  const selectedUnitNumberDescription = useSelector(
    selectConfigExpressApplyUnitNumberDescription
  );
  const { property } = useSelector(selectTaxCertApplicationData) ?? {};

  const [selectedRadioOption, setSelectedRadioOption] = useState(
    property ? 'lookup' : ''
  );

  const hasData = useMemo(() => property !== undefined, [property]);

  useEffect(() => {
    if (hasData) {
      setSelectedRadioOption('lookup');
      setIsOptionSelected(true);
      setShowAddressLookup(true);
      setSelectedProperty({
        address: property!.propertyaddress,
        apn: property!.propertyid,
      });
      setApn(property!.propertyid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData]);

  const formModel = useMemo<FormValues>(() => {
    return {
      propertyaddress: property?.propertyaddress ?? '',
      propertyid: property?.propertyid ?? '',
      unitnumber: property?.unitnumber ?? '',
    };
  }, [property]);

  const { StringType } = Schema.Types;
  const validationModel = useMemo(() => {
    return {
      propertyaddress: StringType(),
      propertyid: StringType(),
      unitnumber:
        selectedUnitNumberRequired === UNIT_NUMBER_STATUS_ENUM.REQUIRED
          ? StringType().isRequired(
              'Enter a unit number, or 0 if not applicable'
            )
          : StringType(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnitNumberRequired]);

  useEffect(() => {
    setFormValues(formModel);
  }, [formModel, setFormValues]);

  useEffect(() => {
    const onAPNLookup = async () => {
      setParcelLookupLoading(true);
      setParcelNotFound(false);
      setSelectedProperty({});
      try {
        const result = await lookupAPNAddress(debouncedApn as string);
        const address = result?.address;
        if (result && address) {
          setParcelNotFound(false);
          setFormValues((prev) => ({ ...prev, propertyaddress: address }));
          setSelectedProperty({ address, apn: debouncedApn as string });
        } else {
          setParcelNotFound(true);
          setSelectedProperty({});
        }
      } catch {
        setParcelNotFound(true);
        setSelectedProperty({});
      } finally {
        setParcelLookupLoading(false);
      }
    };

    if (
      debouncedApn &&
      (!selectedProperty?.address ||
        selectedProperty?.address !== formValues['propertyaddress']) &&
      selectedProperty?.apn !== debouncedApn
    ) {
      onAPNLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApn]);

  useEffect(() => {
    if ((selectedParcelConfig?.lookup_html?.length ?? 0) > 0) {
      setLookupInstructions(selectedParcelConfig!.lookup_html!);
    } else if ((selectedParcelConfig?.lookup?.length ?? 0) > 0) {
      setLookupInstructions(
        `<a target="_blank" rel="noreferrer" href="${selectedParcelConfig!.lookup}">Lookup on county website</a>`
      );
    }
  }, [selectedParcelConfig]);

  const hideOrShowFieldSelection = (v: string | number) => {
    setIsOptionSelected(true);
    setPageError(false);
    form.current?.reset();
    formModel.propertyaddress = '';
    formModel.propertyid = '';
    formModel.unitnumber = '';
    setFormValues(formModel);
    const val = String(v);
    setShowAddressLookup(val === 'lookup');
    setShowKnownField(val === 'known');
    setSelectedRadioOption(val);
    setSelectedProperty({});
  };

  const dispatchProperty = useCallback(async () => {
    setPageError(false);

    if (parcelLookupLoading) return;

    if (!isOptionSelected) {
      setPageError(true);
      setParcelNotFound(false);
      setErrorMessage('One of the above options must be selected.');
      return;
    }

    if (isNullUndefinedOrEmptyObject(selectedProperty)) {
      setErrorMessage('A property must be selected to continue');
      setParcelNotFound(false);
      setPageError(true);
      return;
    }

    if (!form?.current?.check()) return;

    const _formValues = { ...formValues };
    _formValues['propertyid'] = apn as string;

    const currentProperty = property;
    const newProperty = { ..._formValues };
    const propertyChanged =
      !currentProperty ||
      currentProperty.propertyid !== newProperty.propertyid ||
      currentProperty.unitnumber !== newProperty.unitnumber;

    if (propertyChanged) {
      dispatch(clearTaxCertDataExceptProperty());
    }

    dispatch(updateTaxCertApplicationData({ property: { ..._formValues } }));
    dispatch(
      setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
    );
    dispatch(setCurrentStep(selectedCurrentStep + 1));
    placeNavigate('/' + selectedNextStepPath);
  }, [
    apn,
    dispatch,
    formValues,
    isOptionSelected,
    parcelLookupLoading,
    placeNavigate,
    property,
    selectedCurrentStep,
    selectedNextStepPath,
    selectedProperty,
  ]);

  const handleBackClick = useCallback(() => {
    dispatch(resetTaxCertState());
  }, [dispatch]);

  const onAddressSelected = (propertyInfo: AddressLookupResult | null) => {
    if (propertyInfo) {
      const { address, apn } = propertyInfo;
      const apnString = apn.length > 0 ? apn[0] : '';
      setApn(apnString);
      setFormValues((prev) => ({ ...prev, propertyaddress: address }));
      setSelectedProperty({ address, apn: apnString });
    } else {
      setSelectedProperty({});
    }
  };

  const onPropertyIdChange = (value: string) => {
    if (value?.length > 3) {
      setApn(value);
    }
  };

  return (
    <FlowLayout
      flow={FLOWS_ENUM.TAX_CERTIFICATE}
      title="Property / APN"
      onNext={dispatchProperty}
      onBack={handleBackClick}
      omitNavigationForward
      showNext
      showBack
    >
      <Grid
        className="mt-12 ml-0"
        fluid
      >
        <Row>
          <Col xs={24}>
            <Text align="left">
              We need to know the {selectedParcelConfig?.verbiage} of the
              property you would like to pay. You can type in your{' '}
              {selectedParcelConfig?.verbiage} directly below if you already
              know it, or you can find the {selectedParcelConfig?.verbiage} by
              typing in the property address.
            </Text>
            <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
              <legend className="sr-only">
                Property identification method
              </legend>
              <RadioGroup
                name="radio-option-group"
                defaultValue={selectedRadioOption}
                onChange={hideOrShowFieldSelection}
              >
                <Radio value="known">
                  I know my {selectedParcelConfig?.verbiage}
                </Radio>
                <Radio value="lookup">
                  I want to type in the property address
                </Radio>
              </RadioGroup>
            </fieldset>
            <Form
              className="mt-12"
              ref={form}
              checkTrigger="blur"
              formValue={formValues}
              onCheck={setFormError}
              onChange={(v) => setFormValues(v as FormValues)}
              fluid
            >
              {showAddressLookup && (
                <div data-cy="propertyaddress">
                  <CityRestrictedAddressLookup
                    name="propertyaddress"
                    label="Address of the rental property"
                    errorMessage={formError['propertyaddress']}
                    validationRule={validationModel.propertyaddress}
                    value={formValues['propertyaddress']}
                    onItemSelected={onAddressSelected}
                  />
                </div>
              )}
              {showKnownField && (
                <>
                  <FormTextField
                    name="propertyid"
                    label={selectedParcelConfig?.verbiage}
                    hasError={!!formError['propertyid']}
                    validationRule={validationModel.propertyid}
                    value={formValues['propertyid']}
                    subtitle={() =>
                      lookupInstructions.length > 0 ? (
                        <Text className="mb-8">
                          {constructMarkup(lookupInstructions)}
                        </Text>
                      ) : (
                        <></>
                      )
                    }
                    onChange={onPropertyIdChange}
                    bottomMarginOverrideClass="override-form-field-bottom-margin-4"
                  />
                  {parcelLookupLoading && <IconIndicator />}
                  {parcelNotFound && (
                    <Notice
                      className="mt-16"
                      message={`No address found for ${selectedParcelConfig?.verbiage}: ${apn}`}
                    />
                  )}
                </>
              )}

              {Object.keys(selectedProperty).length > 0 && (
                <>
                  <div className="mv-16">
                    <SelectedProperty
                      propertyDetails={selectedProperty as PropertyDetails}
                    />
                  </div>
                  <FormTextField
                    name="unitnumber"
                    label={`Unit Number ${selectedUnitNumberRequired === UNIT_NUMBER_STATUS_ENUM.REQUIRED ? '(required if exists)' : '(Optional)'}`}
                    subtitle={() =>
                      selectedUnitNumberDescription ? (
                        <Text className="mt-4 mb-8">
                          {selectedUnitNumberDescription}
                        </Text>
                      ) : (
                        <></>
                      )
                    }
                    hasError={!!formError['unitnumber']}
                    errorMessage={formError['unitnumber']}
                    validationRule={validationModel.unitnumber}
                  />
                </>
              )}
            </Form>
            {pageError && (
              <Notice
                className="mt-16"
                message={`${errorMessage}`}
              />
            )}
          </Col>
        </Row>
      </Grid>
    </FlowLayout>
  );
};

export default TaxCertProperty;
